import { useEffect, useState } from "react";
import { Snowflake, Flame, Plus, Timer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi, fmtDT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Empty, Pill } from "@/components/kit";

const RULES = { cooling: "Cool to 8°C or below within 90 minutes, then refrigerate.", reheating: "Reheat until the core is 75°C or above (82°C in Scotland)." };
const minsSince = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

function useTick(ms) {
  const [, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

function StartForm({ venueId, onDone }) {
  const [f, setF] = useState({ kind: "cooling", food: "", start_temp: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cooling", { ...f, venue_id: venueId, start_temp: Number(f.start_temp) });
      toast.success(f.kind === "cooling" ? "Cooling started. We'll time it." : "Reheating started");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4">
      <div className="flex gap-2">{[["cooling", "Cooling", Snowflake], ["reheating", "Reheating", Flame]].map(([k, l, I]) => <button type="button" key={k} data-testid={`cool-kind-${k}`} onClick={() => setF({ ...f, kind: k })} className={`ka-press flex flex-1 items-center justify-center gap-2 rounded-xl border py-3 text-sm font-semibold ${f.kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300"}`}><I className="h-4 w-4" />{l}</button>)}</div>
      <p className="text-sm text-slate-500">{RULES[f.kind]}</p>
      <Field label="Food *"><input data-testid="cool-food-input" required maxLength={120} className="ka-input" placeholder="e.g. Beef stew" value={f.food} onChange={(e) => setF({ ...f, food: e.target.value })} /></Field>
      <Field label="Starting temperature °C *"><input data-testid="cool-start-temp-input" required type="number" step="0.1" inputMode="decimal" className="ka-input" value={f.start_temp} onChange={(e) => setF({ ...f, start_temp: e.target.value })} /></Field>
      <button data-testid="cool-start-submit" className="ka-press w-full rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Start the clock</button>
    </form>
  );
}

function FinishForm({ log, onDone }) {
  const [temp, setTemp] = useState("");
  const [action, setAction] = useState("");
  const [needAction, setNeedAction] = useState(false);
  useTick(30000);
  const mins = minsSince(log.start_at);
  const t = Number(temp);
  const fail = needAction || (temp !== "" && (log.kind === "cooling" ? t > 8 || mins > 90 : t < 75));
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/cooling/${log.id}/finish`, { end_temp: t, corrective_action: action });
      toast[data.status === "pass" ? "success" : "warning"](data.status === "pass" ? "Passed and saved" : "Saved as failed with your action");
      onDone();
    } catch (err) {
      if (err.response?.status === 400 && !action.trim()) setNeedAction(true);
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4">
      <p className="text-sm text-slate-500">{RULES[log.kind]} Started {fmtDT(log.start_at)} at {log.start_temp}°C ({mins} min ago).</p>
      <Field label="Temperature now °C *"><input data-testid="cool-end-temp-input" required type="number" step="0.1" inputMode="decimal" className="ka-input" value={temp} onChange={(e) => setTemp(e.target.value)} /></Field>
      {fail && <Field label="What did you do about it? *" hint="e.g. Split into shallow trays and put in the blast chiller, or threw it away"><textarea data-testid="cool-action-input" required rows={2} maxLength={400} className="ka-input" value={action} onChange={(e) => setAction(e.target.value)} /></Field>}
      <button data-testid="cool-finish-submit" className="ka-press w-full rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Save final reading</button>
    </form>
  );
}

function Row({ c, can, onFinish }) {
  const I = c.kind === "cooling" ? Snowflake : Flame;
  useTick(30000);
  const mins = c.status === "in_progress" ? minsSince(c.start_at) : null;
  const late = c.status === "in_progress" && c.kind === "cooling" && mins > 90;
  return (
    <div data-testid={`cool-row-${c.id}`} className={`ka-card flex flex-wrap items-center gap-4 p-4 ${late ? "border-rose-300 bg-rose-50/60" : ""}`}>
      <I className={`h-5 w-5 ${c.kind === "cooling" ? "text-sky-600" : "text-orange-600"}`} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{c.food}</div>
        <div className="text-xs text-slate-500">{fmtDT(c.start_at)} · {c.start_temp}°C → {c.status === "in_progress" ? "…" : `${c.end_temp}°C in ${c.minutes} min`} · {c.started_by}</div>
        {c.corrective_action && <div className="text-xs text-rose-700">Action: {c.corrective_action}</div>}
      </div>
      {c.status === "in_progress" ? <>
        <span data-testid={`cool-timer-${c.id}`} className={`inline-flex items-center gap-1 font-mono text-sm font-bold ${late ? "text-rose-700" : "text-slate-700"}`}><Timer className="h-4 w-4" />{mins} min{late && " · over 90"}</span>
        {can && <button data-testid={`cool-finish-${c.id}`} onClick={() => onFinish(c)} className="ka-press rounded-full bg-slate-950 px-4 py-2 text-xs font-semibold text-white">Take final temp</button>}
      </> : <Pill status={c.status === "pass" ? "ok" : "overdue"} label={c.status === "pass" ? "Pass" : "Fail"} />}
    </div>
  );
}

export default function Cooling() {
  const { venueId, venue, can } = useAuth();
  const { data, reload } = useApi(venueId ? "/cooling" : null, { venue_id: venueId });
  const [dlg, setDlg] = useState(null);
  const done = () => { setDlg(null); reload(); };
  const open = (data || []).filter((c) => c.status === "in_progress");
  const past = (data || []).filter((c) => c.status !== "in_progress");
  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Cooling & reheating" subtitle="Start the clock when hot food comes off the heat. We check it against the 90-minute cooling and 75°C reheating rules."
        actions={can("record") && <button data-testid="cool-new-button" onClick={() => setDlg("start")} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />Start</button>} />
      {!!open.length && <div className="mb-8"><div className="ka-eyebrow mb-3">In progress</div><div className="space-y-2">{open.map((c) => <Row key={c.id} c={c} can={can("record")} onFinish={setDlg} />)}</div></div>}
      <div className="ka-eyebrow mb-3">Finished</div>
      {data && !past.length && <Empty testid="cooling-empty" icon={Snowflake} title="Nothing logged yet" text="Tap Start when you begin cooling or reheating food." />}
      <div className="space-y-2">{past.map((c) => <Row key={c.id} c={c} />)}</div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="cool-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{dlg === "start" ? "Start cooling or reheating" : `Final temperature · ${dlg?.food}`}</DialogTitle></DialogHeader>
          {dlg === "start" ? <StartForm venueId={venueId} onDone={done} /> : dlg && <FinishForm log={dlg} onDone={done} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
